import { useEffect, useState } from "react";
import * as Lucide from "lucide-react";
import { FileText } from "lucide-react";
import { fetchCollection } from "@/lib/cmsApi";

/**
 * AAA B2B compliance strip — CMS managed.
 * Editable at /admin/collections/trust-badges (add, edit, reorder, toggle).
 *
 * Fallback defaults are used only when the CMS collection is empty or
 * unreachable (e.g. SSR/first paint before hydration).
 */
const FALLBACK_BADGES = [
  { label: "Public Liability Insurance", sub: "Insured",        icon: "ShieldCheck",    kind: "acc" },
  { label: "Safety Statement",           sub: "Documented",     icon: "ScrollText",     kind: "doc" },
  { label: "RAMS",                       sub: "Risk + Method",  icon: "FileText",       kind: "doc" },
  { label: "Risk Assessments",           sub: "Per Site",       icon: "ClipboardCheck", kind: "doc" },
  { label: "Method Statements",          sub: "Written",        icon: "BookOpen",       kind: "doc" },
  { label: "SDS",                        sub: "Chemical Sheets",icon: "Droplets",       kind: "doc" },
  { label: "Safe Pass",                  sub: "Trained Staff",  icon: "BadgeCheck",     kind: "acc" },
  { label: "Manual Handling",            sub: "Trained Staff",  icon: "PackageCheck",   kind: "acc" },
  { label: "HACCP Training",             sub: "Food Projects",  icon: "Utensils",       kind: "acc" },
  { label: "Infection Control",          sub: "Healthcare",     icon: "HeartPulse",     kind: "acc" },
];

export default function TrustStrip() {
  const [badges, setBadges] = useState(FALLBACK_BADGES);

  useEffect(() => {
    let cancelled = false;
    fetchCollection("trust-badges")
      .then((rows) => {
        if (cancelled) return;
        const visible = (rows || [])
          .filter((r) => r.is_visible !== false)
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        if (visible.length) setBadges(visible);
      })
      .catch(() => {
        // keep fallback silently
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section
      data-testid="trust-strip"
      aria-label="Documentation, procedures & accreditations"
      className="bg-white border-y border-[#E5EBF2]"
    >
      <div className="container-ags py-8 md:py-10">
        <div className="flex flex-col md:flex-row md:items-start gap-6 md:gap-10">
          <div className="md:w-52 flex-shrink-0">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/55">
              Documentation &amp; Accreditation
            </span>
            <div className="mt-1 font-heading font-bold text-sm text-[#0E2A57] leading-tight">
              Every site backed by<br />written procedures &amp; risk control
            </div>
          </div>

          <div className="flex-1 grid grid-cols-3 md:grid-cols-6 lg:grid-cols-6 gap-px bg-[#E5EBF2]">
            {badges.map(({ label, sub, kind, icon }) => {
              const Icon = Lucide[icon] || FileText;
              return (
                <div
                  key={label}
                  data-testid={`trust-badge-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="bg-white px-2 py-3 flex flex-col items-center text-center group hover:bg-[#F4F8FC] transition-colors"
                >
                  <Icon
                    size={16}
                    strokeWidth={1.5}
                    className={
                      kind === "acc"
                        ? "text-[#2BB6E5]"
                        : kind === "risk"
                        ? "text-[#FFB836]"
                        : "text-[#0E2A57] group-hover:text-[#2BB6E5] transition-colors duration-500"
                    }
                  />
                  <div className="mt-1.5 font-heading font-bold text-[10px] tracking-tight text-[#0E2A57] leading-tight">
                    {label}
                  </div>
                  {sub && (
                    <div className="mt-0.5 font-mono text-[8px] uppercase tracking-[0.15em] text-[#475569]">
                      {sub}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
